import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _setTimeout from "@babel/runtime-corejs3/core-js/set-timeout";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js/instance/for-each";
import _Object$assign from "@babel/runtime-corejs3/core-js/object/assign";
import _Array$isArray from "@babel/runtime-corejs3/core-js/array/is-array";
import _Set from "@babel/runtime-corejs3/core-js/set";
import _everyInstanceProperty from "@babel/runtime-corejs3/core-js/instance/every";
import _findInstanceProperty from "@babel/runtime-corejs3/core-js/instance/find";
import _flatMapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/flat-map";
import React from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import { required, minLength, maxLength, email, pattern } from "../formValidators/index.js";
import createErrors from "../createErrors/index.js";
const schedulePreventable = callback => {
  const timeoutId = _setTimeout(() => {
    callback();
  });
  return () => {
    clearTimeout(timeoutId);
  };
};
const getStaticPropsFromValidators = validators => {
  const props = {};
  _forEachInstanceProperty(validators).call(validators, validator => {
    _Object$assign(props, validator.staticProps || {});
  });
  return props;
};
const maybeCallFunction = function (prop) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  return typeof prop === 'function' ? prop(...args) : prop;
};
const defaultSubTextOnInvalid = _ref => {
  let {
    errors
  } = _ref;
  return errors[0].data;
};
const Context = /*#__PURE__*/React.createContext({
  bindField: noop,
  unbindField: noop
});
const useRenderRef = val => {
  const ref = React.useRef(val);
  ref.current = val;
  return ref;
};
const normalizePatternArgs = patternParam => {
  let args;
  if (_Array$isArray(patternParam)) {
    args = patternParam;
  } else if (typeof patternParam === 'object' && !(patternParam instanceof RegExp)) {
    args = [patternParam.test, patternParam.patternName, patternParam.message];
  } else {
    args = [patternParam];
  }
  return args;
};
const Form = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    onSubmit = noop,
    children,
    ...restOfProps
  } = _ref2;
  const fieldBindingsRef = React.useRef(new _Set());
  const bindField = binding => {
    fieldBindingsRef.current.add(binding);
  };
  const unbindField = binding => {
    fieldBindingsRef.current.delete(binding);
  };
  const handleLeavingOfAllFields = () => {
    var _context;
    _forEachInstanceProperty(_context = fieldBindingsRef.current).call(_context, fieldBinding => {
      fieldBinding.handleErrorsOnLeave();
    });
  };
  const handleSubmit = evt => {
    var _context2;
    /**
     * Prevent default needs to be the very first thing. See note #1 above.
     */
    evt.preventDefault();
    const valid = _everyInstanceProperty(_context2 = [...fieldBindingsRef.current]).call(_context2, _ref3 => {
      let {
        errorsRef
      } = _ref3;
      return errorsRef.current.length === 0;
    });
    const validate = () => {
      if (!valid) {
        var _context3;
        handleLeavingOfAllFields();
        const firstFocusableFieldWithErrorsBinding = _findInstanceProperty(_context3 = [...fieldBindingsRef.current]).call(_context3, _ref4 => {
          let {
            errorsRef,
            elementRef
          } = _ref4;
          return errorsRef.current.length > 0 && elementRef.current;
        });
        if (firstFocusableFieldWithErrorsBinding) {
          firstFocusableFieldWithErrorsBinding.elementRef.current.focus();
        }
      }
    };
    const preventValidation = schedulePreventable(validate);
    const pseudoEvent = {
      valid,
      validate,
      preventValidation
    };
    onSubmit(pseudoEvent, evt);
  };
  const context = {
    bindField,
    unbindField
  };
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement("form", _extends({
    ref: ref,
    noValidate: true,
    onSubmit: handleSubmit
  }, restOfProps), children));
});
process.env.NODE_ENV !== "production" ? Form.propTypes = {
  /**
   * Called when form is submitted. Is given a custom event object that allows controlling validation behavior
   */
  onSubmit: PropTypes.func,
  /**
   * Form contents
   */
  children: PropTypes.node
} : void 0;
const useInput = _ref5 => {
  let {
    defaultValue,
    value,
    name,
    validations,
    ref,
    onChange: givenOnChange = noop,
    onBlur: givenOnBlur = noop,
    subText = '',
    subTextOnValid = subText,
    subTextOnInvalid = defaultSubTextOnInvalid
  } = _ref5;
  const [visualState, setVisualState] = React.useState('neutral');
  const visualStateRef = useRenderRef(visualState);
  const fallbackRef = React.useRef();
  const errorsRef = React.useRef([]);
  const finalElementRef = ref || fallbackRef;
  const isValueControlledRef = React.useRef(value !== undefined);
  let onChange = givenOnChange;
  let controlledOrUncontrolledValue;
  const handleErrorsOnChange = () => {
    if (visualStateRef.current === 'invalid' && errorsRef.current.length === 0) {
      setVisualState('valid');
    } else if (visualStateRef.current === 'valid' && errorsRef.current.length > 0) {
      setVisualState('invalid');
    }
  };
  const handleErrorsOnLeave = React.useCallback(() => {
    if (errorsRef.current.length > 0) {
      setVisualState('invalid');
    } else {
      setVisualState('neutral');
    }
  }, []);
  if (isValueControlledRef.current) {
    controlledOrUncontrolledValue = value;
  } else {
    /*
      Conditional call of react hooks is generally unsafe but
      it is completely safe here because it is dependent on a ref
      value that never changes
    */
    /* eslint-disable react-hooks/rules-of-hooks */
    const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
    controlledOrUncontrolledValue = uncontrolledValue;
    onChange = React.useCallback(evt => {
      setUncontrolledValue(evt.target.value);
      givenOnChange(evt);
    }, [givenOnChange]);
  }
  errorsRef.current = createErrors(name, controlledOrUncontrolledValue, validations);
  handleErrorsOnChange();
  const onBlur = React.useCallback(evt => {
    if (!evt.currentTarget.contains(evt.relatedTarget)) {
      handleErrorsOnLeave();
    }
    givenOnBlur(evt);
  }, [handleErrorsOnLeave, givenOnBlur]);
  const finalProps = {
    ref: finalElementRef,
    defaultValue,
    value,
    name,
    'aria-invalid': visualState === 'invalid' ? 'true' : undefined,
    onBlur,
    onChange,
    ...getStaticPropsFromValidators(validations)
  };
  const form = React.useContext(Context);
  const formRef = useRenderRef(form);
  React.useEffect(() => {
    const formBinding = {
      errorsRef,
      elementRef: finalElementRef,
      handleErrorsOnLeave
    };
    const dereferencedForm = formRef.current;
    dereferencedForm.bindField(formBinding);
    return () => dereferencedForm.unbindField(formBinding);
  }, [finalElementRef, formRef, handleErrorsOnLeave]);
  const getSubtext = () => {
    const errors = errorsRef.current;
    if (visualState === 'valid' && errors.length === 0) {
      return maybeCallFunction(subTextOnValid, {}, subText);
    }
    if (visualState === 'invalid' && errors.length > 0) {
      return maybeCallFunction(subTextOnInvalid, {
        errors
      }, defaultSubTextOnInvalid({
        errors
      }));
    }
    return maybeCallFunction(subText, {
      errors
    }, '');
  };
  return [finalProps, {
    errors: errorsRef.current,
    state: visualState,
    subText: getSubtext()
  }];
};
export const Input = /*#__PURE__*/React.forwardRef((_ref6, ref) => {
  let {
    name,
    value,
    defaultValue,
    validations,
    children,
    onBlur,
    onChange,
    subText,
    subTextOnValid,
    subTextOnInvalid,
    /** No validation only props */
    state
  } = _ref6;
  const validationsAreBeingUsed = !!validations;
  const validationsEnabledRef = React.useRef(validationsAreBeingUsed);
  if (!validationsEnabledRef.current) {
    if (validationsAreBeingUsed) {
      process.env.NODE_ENV !== 'production' ? console.warn('Validations are being passed after being initialized as disabled. Probably means the component is being misused.') : void 0;
    }
    if (subTextOnValid || subTextOnInvalid) {
      process.env.NODE_ENV !== 'production' ? console.warn('Validations are disabled, yet `subTextOnValid` or `subTextOnInvalid` props were passed. These are only used when validations are enabled.') : void 0;
    }
    return children({
      onChange,
      onBlur,
      name,
      ref,
      value,
      defaultValue
    }, {
      state,
      subText,
      errors: []
    });
  }
  if (state) {
    process.env.NODE_ENV !== 'production' ? console.warn('Validations are enabled. However, it is also receiving a `state` prop which no effect on the validation visual states. This possibly means this component is being misused.') : void 0;
  }
  if (!validationsAreBeingUsed) {
    process.env.NODE_ENV !== 'production' ? console.warn('Validations are being removed after being initialized as enabled. Probably means the component is being misused.') : void 0;
  }
  const finalValidations = React.useMemo(() => {
    const validationsFromConvenienceProp = validationValue => {
      const validationList = [];
      if (validationValue.required === true) {
        validationList.push(required());
      }
      if (validationValue.email === true) {
        validationList.push(email());
      }
      if (typeof validationValue.pattern !== 'undefined') {
        const args = normalizePatternArgs(validationValue.pattern);
        validationList.push(pattern(...args));
      }
      if (typeof validationValue.minLength === 'number') {
        validationList.push(minLength(validationValue.minLength));
      }
      if (typeof validationValue.maxLength === 'number') {
        validationList.push(maxLength(validationValue.maxLength));
      }
      return validationList;
    };
    const givenValidations = validations || [];
    const normalizedGivenValidations = _Array$isArray(givenValidations) ? givenValidations : [givenValidations];
    return _flatMapInstanceProperty(normalizedGivenValidations).call(normalizedGivenValidations, validation => {
      if (typeof validation.validate === 'function') {
        return validation;
      }
      return validationsFromConvenienceProp(validation);
    });
  }, [validations]);
  const [props, data] = useInput({
    ref,
    name,
    value,
    defaultValue,
    validations: finalValidations,
    onBlur,
    onChange,
    subText,
    subTextOnValid,
    subTextOnInvalid
  });
  return children(props, data);
});
const tupleType = typeCheckers => PropTypes.arrayOf(function (value, index) {
  for (var _len2 = arguments.length, rest = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
    rest[_key2 - 2] = arguments[_key2];
  }
  return typeCheckers[index].call(PropTypes, value, index, ...rest);
});
Input.propTypes = {
  /**
   * Field name
   */
  name: PropTypes.string,
  /**
   * Value of input if you wish to enable controlled mode
   */
  value: PropTypes.string,
  /**
   * Default value for uncontrolled mode
   */
  defaultValue: PropTypes.string,
  /**
   * Validations to apply
   */
  validations: PropTypes.oneOfType([PropTypes.shape({
    required: PropTypes.bool,
    email: PropTypes.bool,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    pattern: PropTypes.oneOfType([tupleType([PropTypes.shape({
      test: PropTypes.func
    }), PropTypes.string]), PropTypes.shape({
      test: PropTypes.func
    }), PropTypes.shape({
      test: PropTypes.shape({
        test: PropTypes.func
      }),
      patternName: PropTypes.string,
      message: PropTypes.string
    })])
  }), PropTypes.shape({
    staticProps: PropTypes.shape(),
    name: PropTypes.string.isRequired,
    validate: PropTypes.func.isRequired
  }), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    required: PropTypes.bool,
    email: PropTypes.bool,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    pattern: PropTypes.oneOfType([tupleType([PropTypes.shape({
      test: PropTypes.func
    }), PropTypes.string]), PropTypes.shape({
      test: PropTypes.func
    })])
  }), PropTypes.shape({
    staticProps: PropTypes.shape(),
    name: PropTypes.string.isRequired,
    validate: PropTypes.func.isRequired
  })]))]),
  /**
   * Called on blur. Merged with onBlur provided by this component
   */
  onBlur: PropTypes.func,
  /**
   * Called on change. Merged with onChange provided by this component
   */
  onChange: PropTypes.func,
  /**
   * Sub text to render.
   * This is some generic messaging that the rendered component chooses how it should be rendered.
   * This is used when the visual state is `neutral` or when the visual state is `valid` and a
   * subTextOnValid was not provided. When a function is provided, current errors are passed to it.
   */
  subText: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Sub text to render when visual state is valid.
   */
  subTextOnValid: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Sub text to render when visual state is invalid. Defaults to rendering the first error.
   */
  subTextOnInvalid: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Render function. Receives the generated props and the error state.
   *
   * ```
   * (nativeProps, { errors, state }) => React.node
   * ```
   */
  children: PropTypes.func.isRequired,
  /**
   * Force the state. Will only be used if validations are disabled.
   */
  state: PropTypes.oneOf(['valid', 'invalid', 'neutral'])
};
export default Form;